<template>
  <div class="mx-auto mxw-800">
    <v-btn
      v-if="store.state.profile"
      :ripple="false"
      :to="{ name: 'Dashboard' }"
      class="bc-body-sm px-0 mt-3"
      prepend-icon="keyboard_backspace"
      variant="text"
      tracked
    >
      {{ $bct('Back') }}
    </v-btn>

    <v-card
      v-if="contact"
      class="mt-3"
      border
      flat
      tile
    >
      <v-card-title class="fs-24 fw-500 mb-2">
        {{ $bct('Manage email preferences') }}
      </v-card-title>
      <v-card-subtitle class="fs-16">
        {{ $bct('You can opt in or out of receiving specific groups of emails.') }}
      </v-card-subtitle>
      <v-card-text>
        <v-row class="fs-14 fw-600">
          <v-col
            cols="12"
            sm="6"
          >
            {{ $bct('Email group') }}
          </v-col>
          <v-col
            class="d-flex justify-end"
            cols="12"
            sm="6"
          >
            {{ $bct('Receive emails?') }}
          </v-col>
        </v-row>
        <div v-if="role === 'manager'">
          <v-divider class="my-4" />
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <div class="fs-16 mb-2">
                {{ $bct('Availability reminder') }}
              </div>
              <div class="fs-14 c-light-black">
                {{ $bct('This is a weekly email reminding you to update your openings.') }}
              </div>
            </v-col>
            <v-col
              class="d-flex justify-end"
              cols="12"
              sm="6"
            >
              <v-switch
                v-model="contact.availability_reminders"
                hide-details
                mandatory
              />
            </v-col>
          </v-row>
        </div>
        <v-divider class="my-4" />
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <div class="fs-16 mb-2">
              {{ $bct('Messaging') }}
            </div>
            <div class="fs-14 c-light-black">
              {{
                $bct(
                  "Receive notifications when someone messages you. If you opt out, you won't be able to receive these emails.",
                )
              }}
            </div>
          </v-col>
          <v-col
            class="d-flex justify-end"
            cols="12"
            sm="6"
          >
            <v-switch
              v-model="contact.provider_inquiries"
              hide-details
              mandatory
            />
          </v-col>
        </v-row>
        <v-divider class="my-4" />
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <div class="fs-16 mb-2">
              {{ $bct('Unsubscribe all') }}
            </div>
            <div class="fs-14 c-light-black">
              {{
                $bct(
                  'Unsubscribe from all emails. You will continue to receive login and password reset emails when you request them.',
                )
              }}
            </div>
          </v-col>
          <v-col
            class="d-flex justify-end"
            cols="12"
            sm="6"
          >
            <v-checkbox
              v-model="contact.unsubscribe_all"
              hide-details
              mandatory
            />
          </v-col>
        </v-row>
        <v-divider class="my-4" />
        <v-row dense>
          <v-col cols="12">
            <div class="fs-16">
              {{ $bct('Confirm your email address to update preferences:') }}
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="12"
          >
            <v-text-field
              v-model="email"
              variant="filled"
              block
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn
          @click="save"
          :disabled="!email"
          class="w-100pc"
          color="primary"
        >
          <span>{{ $bct('Save') }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup>
import { getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const { api } = useApi();
const $bct = inject('translator');
const store = useStore();
const route = useRoute();
const eventBus = useEventBus();
const instance = getCurrentInstance();

const role = instance?.appContext.config.globalProperties.$role;

const contact = ref({
  availability_reminders: true,
  provider_inquiries: true,
  unsubscribe_all: false,
});
const email = ref(null);

function load() {
  if (email.value) {
    api.contact.get(email.value, (resp) => {
      contact.value = resp.data;
    });
  }
}

function save() {
  api.contact.update(email.value, contact.value, () => {
    eventBus.chime('Email preferences saved');
  });
}

onMounted(() => {
  store.dispatch('identify', {
    success() {
      email.value = store.state.profile ? store.state.profile.email : route.query.email;
      load();
    },
    failure() {
      email.value = route.query.email;
      load();
    },
  });
});
</script>
